import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { VulnerabilitiesService } from '../../../services/vulnerabilities/vulnerabilities.service';
import { PagedResponse } from '../general/models/general';
import {
  IAssociatedEntities,
  IVulnerability,
  IVulnerabilityAsset,
  IVulnerabilityOccurence,
  IVulnerabilityOverview,
} from './models/Vulnerability';
import * as actions from './vulnerabilities.actions';
import * as selectors from './vulnerabilities.selectors';

@Injectable()
export class VulnerabilitiesEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: VulnerabilitiesService,
    private readonly store: Store,
  ) {}

  getVulnerabilities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getVulnerabilities),
      exhaustMap(({ sortOptions, searchTerm, filters, pageNumber, query }) =>
        this.service
          .getVulnerabilities({
            ...sortOptions,
            searchTerm,
            filters,
            pageNumber,
            query,
          })
          .pipe(
            map((pagedVulnerabilities: PagedResponse<IVulnerability>) => {
              return actions.getVulnerabilitiesSuccess({
                pagedVulnerabilities,
              });
            }),
            catchError((error: any) =>
              of(actions.getVulnerabilitiesFailure(error)),
            ),
          ),
      ),
    ),
  );

  getVulnerabilityById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getVulnerabilityById),
      exhaustMap((action) =>
        this.service.getVulnerabilityById(action.id).pipe(
          map((vulnerabilityById: IVulnerabilityOverview) => {
            return actions.getVulnerabilityByIdSuccess({ vulnerabilityById });
          }),
          catchError((error: any) =>
            of(actions.getVulnerabilityByIdFailure(error)),
          ),
        ),
      ),
    ),
  );

  vulnerabilityAssociatedEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.vulnerabilityAssociatedEntities),
      switchMap((action) =>
        this.service
          .getVulnerabilityAssociatedEntities(action.vulnerabilityId)
          .pipe(
            map((associatedEntities: IAssociatedEntities) => {
              return actions.vulnerabilityAssociatedEntitiesSuccess({
                associatedEntities,
              });
            }),
            catchError((error: any) =>
              of(actions.vulnerabilityAssociatedEntitiesFailure(error)),
            ),
          ),
      ),
    ),
  );

  resolveVulnerability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.resolveVulnerability),
      exhaustMap((action) =>
        this.service.resolveVulnerability(action.vulnerabilityId).pipe(
          map(() => {
            return actions.resolveVulnerabilitySuccess({
              vulnerabilityId: action.vulnerabilityId,
            });
          }),
          catchError((error: any) =>
            of(actions.resolveVulnerabilityFailure(error)),
          ),
        ),
      ),
    ),
  );

  resolveVulnerabilitySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.resolveVulnerabilitySuccess),
      mergeMap(({ vulnerabilityId }) =>
        from([
          actions.getVulnerabilityById({ id: vulnerabilityId }),
          actions.getVulnerabilities({
            pageNumber: 1,
            query: { resolved: 'false' },
          }),
        ]),
      ),
    ),
  );

  unresolveVulnerability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.unresolveVulnerability),
      exhaustMap((action) =>
        this.service.unresolveVulnerability(action.vulnerabilityId).pipe(
          map(() => {
            return actions.unresolveVulnerabilitySuccess({
              vulnerabilityId: action.vulnerabilityId,
            });
          }),
          catchError((error: any) =>
            of(actions.resolveVulnerabilityFailure(error)),
          ),
        ),
      ),
    ),
  );

  unresolveVulnerabilitySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.unresolveVulnerabilitySuccess),
      mergeMap(({ vulnerabilityId }) =>
        from([
          actions.getVulnerabilityById({ id: vulnerabilityId }),
          actions.getVulnerabilities({
            pageNumber: 1,
            query: { resolved: 'true' },
          }),
        ]),
      ),
    ),
  );

  getVulnerabilitesAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getVulnerabilitiesAssets),
      exhaustMap(() =>
        this.service.getVulnerabilitiesAssets().pipe(
          map((vulnerabilitiesAssets: IVulnerabilityAsset[]) => {
            return actions.getVulnerabilitiesAssetsSuccess({
              vulnerabilitiesAssets,
            });
          }),
          catchError((error: any) =>
            of(actions.getVulnerabilitiesAssetsFailure(error)),
          ),
        ),
      ),
    ),
  );

  calculateVulnerabilityRisk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.calculateVulnerabilityRisk),
      exhaustMap((action) =>
        this.service.calculateVulnerabilityRisk(action.vulnerabilityId).pipe(
          map(() => {
            return actions.calculateVulnerabilityRiskSuccess({
              vulnerabilityId: action.vulnerabilityId,
            });
          }),
          catchError((error: any) =>
            of(actions.calculateVulnerabilityRiskFailure(error)),
          ),
        ),
      ),
    ),
  );

  getVulnerabilityOccurences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getVulnerabilityOccurences),
      exhaustMap(({ vulnerabilityId, pageNumber }) =>
        this.service
          .getVulnerabilityOccurences(vulnerabilityId, {
            pageNumber,
          })
          .pipe(
            map(
              (
                pagedVulnerabilityOccurences: PagedResponse<IVulnerabilityOccurence>,
              ) => {
                return actions.getVulnerabilityOccurencesSuccess({
                  pagedVulnerabilityOccurences,
                });
              },
            ),
            catchError((error: any) =>
              of(actions.getVulnerabilityOccurencesFailure(error)),
            ),
          ),
      ),
    ),
  );

  resolveOccurrence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.resolveOccurrence),
      exhaustMap((action) =>
        this.service
          .resolveOccurrence(action.vulnerabilityId, action.occurrenceId)
          .pipe(
            map(() => {
              return actions.resolveOccurrenceSuccess({
                vulnerabilityId: action.vulnerabilityId,
                occurrenceId: action.occurrenceId,
              });
            }),
            catchError((error: any) =>
              of(actions.resolveOccurrenceFailure(error)),
            ),
          ),
      ),
    ),
  );

  resolveOccurrenceSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.resolveOccurrenceSuccess),
      withLatestFrom(this.store.select(selectors.getVulnerabilitiesOptions)),
      mergeMap(([{ vulnerabilityId }, options]) =>
        from([
          actions.getVulnerabilityById({ id: vulnerabilityId }),
          actions.getVulnerabilityOccurences({
            vulnerabilityId,
            pageNumber: options.pageNumber,
          }),
        ]),
      ),
    ),
  );

  unresolveOccurrence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.unresolveOccurrence),
      exhaustMap((action) =>
        this.service
          .unResolveOccurrence(action.vulnerabilityId, action.occurrenceId)
          .pipe(
            map(() => {
              return actions.unresolveOccurrenceSuccess({
                vulnerabilityId: action.vulnerabilityId,
                occurrenceId: action.occurrenceId,
              });
            }),
            catchError((error: any) =>
              of(actions.unresolveOccurrenceFailure(error)),
            ),
          ),
      ),
    ),
  );

  unResolveOccurrenceSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.unresolveOccurrenceSuccess),
      withLatestFrom(this.store.select(selectors.getVulnerabilitiesOptions)),
      mergeMap(([{ vulnerabilityId }, options]) =>
        from([
          actions.getVulnerabilityById({ id: vulnerabilityId }),
          actions.getVulnerabilityOccurences({
            vulnerabilityId,
            pageNumber: options.pageNumber,
          }),
        ]),
      ),
    ),
  );
}
